import { Controller } from 'stimulus';

// When a capability checkbox is checked, auto-check the default behaviour
// checkboxes defined by DEFAULT_BEHAVIOURS_FOR_CAPABILITY.
//
// Also disables behaviour checkboxes that require certain capabilities to be
// active (BEHAVIOUR_REQUIRED_CAPABILITIES), showing a hint when disabled.
//
// Usage:
//   Wrap the form with data-controller="capability-default-behaviours"
//   and pass the mappings as JSON values:
//     data-capability-default-behaviours-mapping-value='{"contact":[1,2],"button":[2]}'
//     data-capability-default-behaviours-required-capabilities-value='{"3":{"capabilities":["contact","button"],"hint":"Requires..."}}'
//
//   On each capability checkbox, add:
//     data-action="change->capability-default-behaviours#capabilityChanged"
//     data-capability-key="contact"
export default class extends Controller {
  static values = {
    mapping: Object, // { capabilityKey: [behaviourId, ...] }
    requiredCapabilities: Object, // { behaviourId: { capabilities: [...], hint: "..." } }
    enablingBehaviours: Object, // { behaviourId: [enablingBehaviourId, ...] }
  };

  connect() {
    this.updateRequiredCapabilities();
    this._bindBehaviourCheckboxes();
  }

  _bindBehaviourCheckboxes() {
    this._behaviourCheckboxes().forEach((cb) => {
      cb.addEventListener('change', this._onBehaviourChanged);
    });
  }

  _onBehaviourChanged = () => {
    this.updateRequiredCapabilities();
  };

  _behaviourCheckboxes() {
    return this.element.querySelectorAll(
      'input[type="checkbox"][name="device[behaviour_ids][]"]'
    );
  }

  disconnect() {
    this._behaviourCheckboxes().forEach((cb) => {
      cb.removeEventListener('change', this._onBehaviourChanged);
    });
  }

  capabilityChanged(event) {
    const checkbox = event.target;

    if (checkbox.checked) {
      const capabilityKey = checkbox.dataset.capabilityKey;
      if (capabilityKey) {
        const behaviourIds = this.mappingValue[capabilityKey];
        if (behaviourIds) {
          behaviourIds.forEach((id) => {
            const behaviourCheckbox = this.element.querySelector(
              `input[type="checkbox"][name="device[behaviour_ids][]"][value="${id}"]`
            );
            if (behaviourCheckbox && !behaviourCheckbox.checked) {
              behaviourCheckbox.checked = true;
            }
          });
        }
      }
    }

    this.updateRequiredCapabilities();
  }

  updateRequiredCapabilities() {
    const requiredCapabilities = this.requiredCapabilitiesValue;
    if (!requiredCapabilities || Object.keys(requiredCapabilities).length === 0)
      return;

    Object.entries(requiredCapabilities).forEach(([behaviourId, config]) => {
      const behaviourCheckbox = this.element.querySelector(
        `input[type="checkbox"][name="device[behaviour_ids][]"][value="${behaviourId}"]`
      );
      if (!behaviourCheckbox) return;

      const hasRequiredCapability = config.capabilities.some((capKey) => {
        const capCheckbox = this.element.querySelector(
          `input[data-capability-key="${capKey}"]`
        );
        return capCheckbox && capCheckbox.checked;
      });

      const enablingBehaviourIds =
        this.enablingBehavioursValue[behaviourId] || [];
      const hasEnablingBehaviour = enablingBehaviourIds.some((ebId) => {
        const ebCheckbox = this.element.querySelector(
          `input[type="checkbox"][name="device[behaviour_ids][]"][value="${ebId}"]`
        );
        return ebCheckbox && ebCheckbox.checked;
      });

      const wrapper =
        behaviourCheckbox.closest('.form-check') ||
        behaviourCheckbox.parentElement;

      if (hasRequiredCapability || hasEnablingBehaviour) {
        behaviourCheckbox.disabled = false;
        const hint = wrapper.querySelector('.capability-required-hint');
        if (hint) hint.remove();
      } else {
        behaviourCheckbox.disabled = true;
        behaviourCheckbox.checked = false;

        if (!wrapper.querySelector('.capability-required-hint')) {
          const hint = document.createElement('small');
          hint.className = 'capability-required-hint text-muted d-block';
          hint.textContent = config.hint;
          wrapper.appendChild(hint);
        }
      }
    });
  }
}
